import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Program, Track } from '../data/mockData';
import { Upload, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useUploadThumbnailMutation, API_BASE_URL } from '../../store/api';

const TITLE_MAX_LENGTH = 80;
const THUMBNAIL_MAX_SIZE = 25 * 1024 * 1024; // 25MB

interface CreateProgramDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: (program: Omit<Program, 'id' | 'channelId'>) => void;
  tracks: Track[];
  channelId: number | string;
  onUploadTrack: (file: File) => void;
  initialDateTime?: Date;
  initialData?: Omit<Program, 'id' | 'channelId'> | null;
}

export function CreateProgramDrawer({
  isOpen,
  onClose,
  onContinue,
  tracks,
  channelId,
  onUploadTrack,
  initialDateTime,
  initialData,
}: CreateProgramDrawerProps) {
  const [uploadThumbnail] = useUploadThumbnailMutation();
  const [title, setTitle] = useState('');
  const [trackId, setTrackId] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const prevOpenRef = useRef(false);

  // Restore data when re-opening with initialData (e.g. back from Schedule)
  if (isOpen && !prevOpenRef.current && initialData) {
    setTitle(initialData.title || '');
    setTrackId(initialData.trackId ? String(initialData.trackId) : '');
    setDescription(initialData.description || '');
    setThumbnailPreview(initialData.thumbnail || null);
  }
  prevOpenRef.current = isOpen;

  const handleContinue = () => {
    if (!title.trim()) {
      toast.error('Program title is required');
      return;
    }

    const start = initialDateTime ?? new Date();

    onContinue({
      title,
      trackId: trackId || null,
      startDateTime: start,
      endDateTime: new Date(start.getTime() + 60 * 60 * 1000),
      scheduleType: 'one-time',
      description: description || null,
      thumbnail: thumbnailPreview || null,
    });
  };

  const resetForm = () => {
    setTitle('');
    setTrackId('');
    setDescription('');
    setThumbnailPreview(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleUploadTrack = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onUploadTrack) {
      if (!file.name.toLowerCase().endsWith('.mp3')) {
        toast.error('Only MP3 files are allowed');
        event.target.value = '';
        return;
      }

      const maxSize = 200 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File size must be less than 200 MB');
        event.target.value = '';
        return;
      }

      onUploadTrack(file);
      toast.success(`Track uploaded: ${file.name}`);
      event.target.value = '';
    }
  };

  const handleThumbnailUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      event.target.value = '';
      return;
    }

    if (file.size > THUMBNAIL_MAX_SIZE) {
      toast.error('Thumbnail must be less than 25 MB');
      event.target.value = '';
      return;
    }

    try {
      const result = await uploadThumbnail({ channelId, file }).unwrap();
      setThumbnailPreview(result.url);
    } catch {
      toast.error('Failed to upload thumbnail');
    }
    event.target.value = '';
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-gray-900 border-gray-800 text-white overflow-y-auto p-0"
      >
        <SheetHeader className="px-6 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 border-b border-gray-800">
          <SheetTitle className="text-2xl font-bold text-white">Create Program</SheetTitle>
          <SheetDescription className="text-gray-400 mt-2">
            Enter program details and optionally select a track
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-6">
          {/* Program Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-semibold text-white">
              Program Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              placeholder="Enter program title"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX_LENGTH))}
              maxLength={TITLE_MAX_LENGTH}
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-11"
            />
            <p className={`text-xs ${title.length >= TITLE_MAX_LENGTH ? 'text-amber-400' : 'text-gray-500'}`}>
              {title.length}/{TITLE_MAX_LENGTH} characters
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold text-white">
              Description
            </Label>
            <Textarea
              id="description"
              placeholder="Enter program description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 min-h-[100px] resize-none"
            />
            <p className="text-xs text-gray-500">{description.length}/2000 characters</p>
          </div>

          {/* Select Track File (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="track" className="text-sm font-semibold text-white">
              Track File <span className="text-gray-500 font-normal">(optional)</span>
            </Label>
            <Select value={trackId} onValueChange={setTrackId}>
              <SelectTrigger
                id="track"
                className={`bg-gray-800 border-gray-700 h-11 ${
                  trackId ? 'text-white' : 'text-gray-400'
                }`}
              >
                <SelectValue placeholder="None (no track)" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800">
                <SelectItem
                  value="__none__"
                  className="text-gray-400 focus:bg-gray-800 focus:text-gray-300"
                >
                  None
                </SelectItem>
                {tracks.map((track) => (
                  <SelectItem
                    key={track.id}
                    value={String(track.id)}
                    className="text-white focus:bg-gray-800 focus:text-white"
                  >
                    {track.fileName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!trackId || trackId === '__none__' ? (
              <p className="text-xs text-yellow-400/80">
                Programs without a track will be auto-removed at their start time.
              </p>
            ) : null}
          </div>

          {/* Thumbnail */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-white">
              Thumbnail <span className="text-gray-500 font-normal">(optional)</span>
            </Label>
            {thumbnailPreview ? (
              <div className="relative inline-block">
                <img
                  src={thumbnailPreview.startsWith('/') ? `${API_BASE_URL}${thumbnailPreview}` : thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-32 h-32 rounded-lg object-cover border border-gray-700"
                />
                <button
                  type="button"
                  onClick={() => setThumbnailPreview(null)}
                  className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 transition-colors"
                >
                  <X className="size-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => thumbnailInputRef.current?.click()}
                className="flex items-center gap-3 w-full p-4 rounded-lg border border-dashed border-gray-700 hover:border-gray-500 bg-gray-800/50 hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-300"
              >
                <ImagePlus className="size-5" />
                <span className="text-sm">Upload thumbnail image</span>
              </button>
            )}
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/*"
              onChange={handleThumbnailUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-6 sm:px-8 py-4 sm:py-5">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-gray-700 text-black hover:bg-gray-800 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!title.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Continue to Schedule
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}